import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { usePractice } from '../context/PracticeContext';
import { useAdoption } from '../context/AdoptionContext';
import Tooltip from '../components/Tooltip';

function Dashboard() {
  const { user } = useAuth();
  const { selectedPracticeId, selectedPractice, needsOnboarding } = usePractice();
  const { startWalkthrough, pageContent } = useAdoption();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  const [aiQueue, setAiQueue] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);

  useEffect(() => {
    if (selectedPracticeId) {
      loadDashboardData();
      loadAiQueueSummary();
      loadAiInsights();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPracticeId]);

  const loadAiInsights = async () => {
    try {
      const result = await api.getAiDashboard(selectedPracticeId);
      if (result.success) {
        setAiInsights(result.data);
      }
    } catch (err) {
      // AI insights are optional — silently fail
    }
  };

  const loadAiQueueSummary = async () => {
    try {
      // Load both pending + in_progress so the banner reflects every item still
      // needing human action (in_progress = claimed but not resolved).
      const [pendingResult, inProgressResult] = await Promise.all([
        api.getAiQueue(selectedPracticeId, { status: 'pending' }),
        api.getAiQueue(selectedPracticeId, { status: 'in_progress' }),
      ]);
      if (pendingResult.success) {
        const pendingItems = pendingResult.data || [];
        const inProgressItems = inProgressResult?.success ? (inProgressResult.data || []) : [];
        setAiQueue({
          summary: pendingResult.summary || { pending: 0, in_progress: 0, resolved_today: 0 },
          recentItems: [...pendingItems, ...inProgressItems].slice(0, 3)
        });
      }
    } catch (err) {
      // Silently fail
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await api.getDashboardStats(selectedPracticeId);
      setStats(response.data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRunCampaigns = async () => {
    if (running) return;

    try {
      setRunning(true);
      await api.runAllCampaigns(selectedPracticeId);
      // Refresh dashboard after running campaigns
      await loadDashboardData();
      alert('Campaigns executed successfully!');
    } catch (err) {
      alert(`Error running campaigns: ${err.message}`);
    } finally {
      setRunning(false);
    }
  };

  if (!selectedPracticeId) {
    return (
      <div className="card">
        <h2>Welcome, {user?.firstName || 'User'}!</h2>
        <p style={{ color: '#666' }}>Please select a practice from the dropdown above to view the dashboard.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  if (error) {
    return <div className="error">Error loading dashboard: {error}</div>;
  }

  if (!stats) {
    return <div className="loading">No data available</div>;
  }

  const { overview, revenue, campaigns } = stats;

  return (
    <div className="dashboard">
      {needsOnboarding && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 20px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe',
          borderRadius: 8, marginBottom: 16, gap: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="#3b82f6">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span style={{ fontSize: 14, color: '#1e40af' }}>
              Your practice setup is incomplete. Complete the onboarding wizard to configure messaging, templates, and more.
            </span>
          </div>
          <button
            onClick={() => navigate('/onboarding')}
            style={{
              padding: '6px 16px', backgroundColor: '#3b82f6', color: '#fff', border: 'none',
              borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            Resume Setup
          </button>
        </div>
      )}

      {aiQueue && (aiQueue.summary.pending > 0 || aiQueue.summary.in_progress > 0) && (
        <div style={{
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          border: '1px solid #f59e0b',
          borderRadius: 10,
          padding: '16px 20px',
          marginBottom: 16,
          cursor: 'pointer',
        }} onClick={() => navigate('/practice-view/ai-queue')}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10,
                background: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                  <path d="M12 2L15 9L22 12L15 15L12 22L9 15L2 12L9 9L12 2Z" />
                </svg>
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#92400e' }}>
                  AI Review Needs Attention
                </div>
                <div style={{ fontSize: 13, color: '#a16207', marginTop: 2 }}>
                  {aiQueue.summary.pending > 0 && (
                    <span><strong>{aiQueue.summary.pending}</strong> pending</span>
                  )}
                  {aiQueue.summary.pending > 0 && aiQueue.summary.in_progress > 0 && ' · '}
                  {aiQueue.summary.in_progress > 0 && (
                    <span><strong>{aiQueue.summary.in_progress}</strong> in progress</span>
                  )}
                  {aiQueue.summary.resolved_today > 0 && (
                    <span> · {aiQueue.summary.resolved_today} resolved today</span>
                  )}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {aiQueue.recentItems.length > 0 && (
                <div style={{ textAlign: 'right', marginRight: 8 }}>
                  {aiQueue.recentItems.map((item, i) => (
                    <div key={item.id} style={{
                      fontSize: 12, color: '#92400e', lineHeight: '18px',
                      maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {item.patient_name || 'Visitor'} — {item.escalation_reason?.replace(/_/g, ' ').toLowerCase()}
                    </div>
                  ))}
                </div>
              )}
              <button style={{
                padding: '8px 16px', borderRadius: 6, border: 'none',
                background: '#92400e', color: '#fff', fontSize: 13, fontWeight: 600,
                cursor: 'pointer', whiteSpace: 'nowrap',
              }}>
                Review Now
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2>Dashboard Overview</h2>
            <p style={{ color: '#666', marginTop: '4px' }}>
              {selectedPractice?.name || 'Selected Practice'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {pageContent?.allWalkthroughs?.find(w => w.title === 'Complete Product Tour') && (
              <button
                className="button"
                style={{ background: '#7c3aed', borderColor: '#7c3aed' }}
                onClick={() => {
                  const tour = pageContent.allWalkthroughs.find(w => w.title === 'Complete Product Tour');
                  if (tour) startWalkthrough(tour);
                }}
              >
                Start Product Tour
              </button>
            )}
            <button
              className="button"
              onClick={handleRunCampaigns}
              disabled={running}
            >
              {running ? 'Running Campaigns...' : 'Run All Campaigns'}
            </button>
          </div>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card blue">
          <h3>Total Patients <Tooltip text="Total number of patients registered under this practice" /></h3>
          <div className="value">{overview.total_patients}</div>
        </div>
        <div className="stat-card green">
          <h3>Active Enrollments <Tooltip text="Patients currently in an active campaign outreach sequence" /></h3>
          <div className="value">{overview.active_enrollments}</div>
          <div className="subvalue">of {overview.total_enrollments} total</div>
        </div>
        <div className="stat-card orange">
          <h3>Revenue Opportunity <Tooltip text="Estimated total revenue from all campaign enrollments" /></h3>
          <div className="value">${revenue.total_opportunity}</div>
        </div>
        <div className="stat-card">
          <h3>Recovered Revenue <Tooltip text="Actual revenue recovered from patients who completed their campaigns" /></h3>
          <div className="value">${revenue.total_recovered}</div>
          <div className="subvalue">{revenue.conversion_rate}% conversion</div>
        </div>
      </div>

      {/* AI Insights Strip */}
      {aiInsights && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '14px', marginBottom: '20px',
        }}>
          {/* High-Risk Appointments */}
          <div
            onClick={() => navigate('/practice-view/ai-insights')}
            style={{
              background: 'linear-gradient(135deg, #fef2f2, #fff1f2)',
              border: '1px solid #fecaca', borderRadius: 10, padding: '16px 18px',
              cursor: 'pointer', transition: 'transform 0.15s',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8, background: '#fee2e2',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </div>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#991b1b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>No-Show Risk</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#dc2626' }}>
              {aiInsights.high_risk_appointments ?? 0}
            </div>
            <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 2 }}>high-risk appointments today</div>
          </div>

          {/* At-Risk Patients (Churn) */}
          <div
            onClick={() => navigate('/practice-view/ai-insights')}
            style={{
              background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
              border: '1px solid #fde68a', borderRadius: 10, padding: '16px 18px',
              cursor: 'pointer', transition: 'transform 0.15s',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8, background: '#fef3c7',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2.5">
                  <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                  <circle cx="8.5" cy="7" r="4"/>
                  <line x1="18" y1="8" x2="23" y2="13"/><line x1="23" y1="8" x2="18" y2="13"/>
                </svg>
              </div>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Churn Risk</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#d97706' }}>
              {aiInsights.at_risk_patients ?? 0}
            </div>
            <div style={{ fontSize: 12, color: '#a16207', marginTop: 2 }}>patients at risk of churning</div>
          </div>

          {/* Frustrated Patients (Sentiment) */}
          <div
            onClick={() => navigate('/practice-view/ai-insights')}
            style={{
              background: 'linear-gradient(135deg, #fdf2f8, #fce7f3)',
              border: '1px solid #fbcfe8', borderRadius: 10, padding: '16px 18px',
              cursor: 'pointer', transition: 'transform 0.15s',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8, background: '#fce7f3',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#db2777" strokeWidth="2.5">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M16 16s-1.5-2-4-2-4 2-4 2"/>
                  <line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/>
                </svg>
              </div>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#9d174d', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Sentiment</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#db2777' }}>
              {aiInsights.frustrated_patients ?? 0}
            </div>
            <div style={{ fontSize: 12, color: '#be185d', marginTop: 2 }}>frustrated patients detected</div>
          </div>

          {/* Revenue Forecast */}
          <div
            onClick={() => navigate('/practice-view/ai-insights')}
            style={{
              background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
              border: '1px solid #a7f3d0', borderRadius: 10, padding: '16px 18px',
              cursor: 'pointer', transition: 'transform 0.15s',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8, background: '#d1fae5',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5">
                  <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
                </svg>
              </div>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#065f46', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Revenue Forecast</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#059669' }}>
              ${aiInsights.revenue_forecast_30d != null ? Number(aiInsights.revenue_forecast_30d).toLocaleString() : '—'}
            </div>
            <div style={{ fontSize: 12, color: '#047857', marginTop: 2 }}>projected next 30 days</div>
          </div>
        </div>
      )}

      <div className="card">
        <h2>Campaign Performance</h2>
        <table className="table">
          <thead>
            <tr>
              <th>Campaign Type</th>
              <th>Total Enrollments <Tooltip text="Number of patients enrolled in this campaign type" /></th>
              <th>Active <Tooltip text="Enrollments currently being processed through the campaign cadence" /></th>
              <th>Completed <Tooltip text="Enrollments where the patient successfully booked or responded" /></th>
              <th>Conversion Rate <Tooltip text="Percentage of enrollments that resulted in a completed outcome" /></th>
              <th>Est. Revenue <Tooltip text="Projected revenue if all enrolled patients return for visits" /></th>
              <th>Recovered <Tooltip text="Actual revenue from patients who completed their campaign" /></th>
            </tr>
          </thead>
          <tbody>
            {campaigns && campaigns.length > 0 ? (
              campaigns.map((campaign) => (
                <tr key={campaign.campaign_type}>
                  <td style={{ textTransform: 'capitalize', fontWeight: 600 }}>
                    {campaign.campaign_type}
                  </td>
                  <td>{campaign.total_enrollments}</td>
                  <td>
                    <span className="badge info">{campaign.active}</span>
                  </td>
                  <td>
                    <span className="badge success">{campaign.completed}</span>
                  </td>
                  <td>{campaign.conversion_rate}%</td>
                  <td>${parseFloat(campaign.total_estimated_revenue || 0).toFixed(2)}</td>
                  <td>${parseFloat(campaign.total_recovered_revenue || 0).toFixed(2)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                  No campaign data available. Run campaigns to see performance metrics.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h2>Quick Stats</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          <div>
            <h3>Total Appointments <Tooltip text="Appointments booked through campaign outreach" /></h3>
            <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#667eea' }}>
              {overview.total_appointments}
            </p>
          </div>
          <div>
            <h3>Messages Sent <Tooltip text="Total SMS and email messages sent across all campaigns" /></h3>
            <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#11998e' }}>
              {overview.total_messages}
            </p>
          </div>
          <div>
            <h3>Conversions <Tooltip text="Number of enrollments that resulted in a successful outcome" /></h3>
            <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#fa709a' }}>
              {revenue.conversions}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
